import {
  DEPENDENT_FAMILY_OPTIONS,
  DependentFamily,
  dependentFamilyOf,
} from "v2/pages/Clients/AddressableSetup/AddressableSetup.dependentFamilies";
import { NO_DEPENDENTS_SENTINEL } from "v2/pages/Clients/AddressableSetup/AddressableSetup.constants";

/**
 * Documentos obrigatórios por parentesco (D41, 28/10/2026). O titular e cada família de
 * dependente da Tabela 07 têm a sua lista; o backend (`documentos-obrigatorios.ts`) exige da
 * linha só a do seu parentesco. Sem mapa, a lista única vale para todos, como antes.
 */
export type Parentesco = "TITULAR" | DependentFamily;
export type MapaPorParentesco = Partial<Record<Parentesco, string[]>>;

export const ROTULO_PARENTESCO: Record<Parentesco, string> = {
  TITULAR: "Titular",
  ...(Object.fromEntries(DEPENDENT_FAMILY_OPTIONS.map((o) => [o.family, o.label])) as Record<DependentFamily, string>),
};

/**
 * Linhas da tabela: o titular e as famílias que o contrato aceita na endereçabilidade.
 * Sem lista configurada, todas; "Nenhum", só o titular. Parentesco que já tem documento
 * gravado e saiu da endereçabilidade continua visível, para não esconder exigência.
 */
export function linhasDeParentesco(allowedDependentTypes: string[] | undefined, mapa?: MapaPorParentesco | null): Parentesco[] {
  const tipos = allowedDependentTypes ?? [];
  const todas = DEPENDENT_FAMILY_OPTIONS.map((o) => o.family);
  const aceitas = tipos.includes(NO_DEPENDENTS_SENTINEL)
    ? []
    : tipos.length === 0
      ? todas
      : todas.filter((f) => tipos.some((t) => dependentFamilyOf(t) === f));
  const gravadas = Object.keys(mapa ?? {}).filter((k): k is DependentFamily => k !== "TITULAR" && (todas as string[]).includes(k));
  return ["TITULAR", ...todas.filter((f) => aceitas.includes(f) || gravadas.includes(f))];
}

const SO_TITULAR = [
  "Termo de Recisão Contrato de Trabalho (TRCT)",
  "Ficha de Registro",
  "Comprovante Vínculo Empregatício",
  "Três últimos olerites",
  "Carta INSS",
];
const FILHOS: Parentesco[] = ["FILHO_ENTEADO", "FILHO_UNIVERSITARIO", "IRMAO_NETO_BISNETO", "MENOR_POBRE"];
const SO_PARA: Record<string, Parentesco[]> = {
  "Certidão de Casamento": ["CONJUGE", "EX_CONJUGE"],
  "União Estável": ["COMPANHEIRO"],
  "Certidão de Nascimento": FILHOS,
  "Caderneta da Criança": FILHOS,
  "Comprovante Matrícula Faculdade": ["FILHO_UNIVERSITARIO"],
  ...Object.fromEntries(SO_TITULAR.map((d) => [d, ["TITULAR"] as Parentesco[]])),
};

/** Grafias antigas gravadas em contratos (mesmo de-para do backend, `documentos-catalogo.ts`). */
const CANONICO: Record<string, string> = {
  "Certidão Nascimento": "Certidão de Nascimento",
  "Comprovaten Matrícula Faculdade": "Comprovante Matrícula Faculdade",
};

/**
 * Sugestão: distribui a lista que o contrato já tem; nunca acrescenta documento.
 * O que é do vínculo vai ao titular, a certidão ao parentesco dela, o resto a todos
 * (RG/CPF, endereço e os formulários, cujo dono varia por operadora).
 */
export function sugestao(documentos: string[] | undefined, linhas: Parentesco[]): MapaPorParentesco {
  return Object.fromEntries(
    linhas.map((p) => [p, (documentos ?? []).filter((d) => { const so = SO_PARA[CANONICO[d] ?? d]; return !so || so.includes(p); })]),
  );
}

export function uniao(mapa: MapaPorParentesco): string[] {
  return [...new Set(Object.values(mapa).flatMap((d) => d ?? []))];
}
